import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getMe, type MeResponse } from '../api/me';
import { SpendChart } from './usage/SpendChart';

export function ProfileView() {
  const { user, userAttributes, getJwtToken } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);

  const email = userAttributes?.email || user?.signInDetails?.loginId || user?.username || '';
  const firstName = userAttributes?.given_name || '';
  const lastName = userAttributes?.family_name || '';
  const initials = firstName
    ? (firstName[0] + (lastName[0] || '')).toUpperCase()
    : email.slice(0, 2).toUpperCase();

  const isAdmin = me?.profile?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;
      const meData = await getMe(token).catch(() => null);
      setMe(meData);
    } catch {
      // Data is optional for profile view
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">My Profile</h1>
      </div>

      <div className="profile__avatar-section">
        <span className="profile__avatar">{initials}</span>
        <div>
          {(firstName || lastName) && (
            <span className="profile__display-name">
              {[firstName, lastName].filter(Boolean).join(' ')}
            </span>
          )}
          {(!firstName && !lastName) && (
            <span className="profile__display-name">{email}</span>
          )}
        </div>
      </div>

      <section className="view__section">
        <h2 className="view__section-title">User Information</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">First Name</span>
            <span className="settings-card__value">
              {firstName || <span className="profile__not-set">Not set</span>}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Last Name</span>
            <span className="settings-card__value">
              {lastName || <span className="profile__not-set">Not set</span>}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Email</span>
            <span className="settings-card__value">{email}</span>
          </div>
        </div>
      </section>

      <section className="view__section">
        <h2 className="view__section-title">Account</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">User ID</span>
            <span className="settings-card__value profile__user-id">
              {user?.userId || ''}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Role</span>
            <span className="settings-card__value">
              <span className={`status-badge status-badge--${isAdmin ? 'active' : 'pending'}`}>
                {me?.profile?.role ?? 'user'}
              </span>
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Status</span>
            <span className="settings-card__value">
              <span className={`status-badge status-badge--${me?.profile?.status === 'active' ? 'active' : 'disabled'}`}>
                {me?.profile?.status ?? 'unknown'}
              </span>
            </span>
          </div>
          {me?.profile?.maxBudget != null && (
            <div className="settings-card__row">
              <span className="settings-card__label">Budget Limit</span>
              <span className="settings-card__value">
                ${me.profile.maxBudget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Registered access info */}
      <section className="view__section">
        <h2 className="view__section-title">Access & Permissions</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">Gateway Access</span>
            <span className="settings-card__value">
              {me?.profile ? (
                <span className="status-badge status-badge--active">Registered</span>
              ) : (
                <span className="status-badge status-badge--pending">Not Registered</span>
              )}
            </span>
          </div>
          {me?.profile && (
            <div className="settings-card__row">
              <span className="settings-card__label">Member Since</span>
              <span className="settings-card__value">
                {new Date(me.profile.createdAt).toLocaleDateString()}
              </span>
            </div>
          )}
          {!isAdmin && (
            <div
              className="settings-card__row settings-card__row--clickable"
              onClick={() => navigate('/admin/request-access')}
            >
              <span className="settings-card__label">Request Role Change</span>
              <span className="settings-card__value">
                Request admin access or elevated permissions
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Self-service spend overview */}
      <section className="view__section">
        <h2 className="view__section-title">My Usage (Last 30 Days)</h2>
        {me?.userSpend?.dailySpend && me.userSpend.dailySpend.length > 0 ? (
          <div className="chart-container">
            <SpendChart
              data={me.userSpend.dailySpend}
              title="Daily Spend"
              color="#00d4ff"
            />
          </div>
        ) : (
          <div className="settings-card settings-card--empty">No usage data available</div>
        )}
        {me?.userSpend && (
          <div className="dashboard-kpis" style={{ marginTop: 12 }}>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                ${(me.userSpend.totalSpend ?? 0).toFixed(2)}
              </span>
              <span className="dashboard-kpi__label">Total Spend</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {(me.userSpend.totalTokens ?? 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi__label">Total Tokens</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {(me.userSpend.totalRequests ?? 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi__label">Total Requests</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
